import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost, errOf } from '../../lib/api';

interface SponsorView {
  id: string;
  company: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  tier_id: string | null;
  stage: string;
  pledge_amount: number;
  paid_amount: number;
  owner_code: string;
  next_action: string;
  next_action_at: string | null;
  note: string;
}
interface BenefitView {
  id: string;
  item: string;
  done: number;
  done_at: string | null;
}

const STAGES = ['lead', 'contacted', 'pitched', 'pledged', 'confirmed', 'paid', 'declined'] as const;
const field = {
  width: '100%',
  borderRadius: 8,
  padding: '6px 10px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-small)',
};
const btn = { background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: 'none', cursor: 'pointer' } as const;
const box = { border: '1px solid var(--color-divider)', background: 'var(--color-card)', color: 'var(--color-text)' } as const;
const money = (n: number): string => `HK$${Number(n || 0).toLocaleString()}`;

/** 贊助商詳情 —— 推進階段 ／ 金額 ／ **權益 checklist（可由方案自動生成）** */
export default function AdminSponsorPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { id = '' } = useParams();
  const [s, setS] = useState<SponsorView | null>(null);
  const [benefits, setBenefits] = useState<BenefitView[]>([]);
  const [tierBenefits, setTierBenefits] = useState<string[]>([]);
  const [item, setItem] = useState('');
  const [sug, setSug] = useState<{ stage: string; next_action: string; reason: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const r = await apiGet<{ sponsor: SponsorView; benefits: BenefitView[]; tierBenefits: string[] }>(`/api/admin/sponsors/${id}`);
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('sp_failed'));
      return;
    }
    setS(r.data.sponsor);
    setBenefits(r.data.benefits);
    setTierBenefits(r.data.tierBenefits);
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    setErr('');
    const r = await apiPatch(`/api/admin/sponsors/${id}`, body);
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('sp_failed'));
      return;
    }
    setMsg(t('sp_saved'));
    await load();
  }

  async function seedBenefits() {
    setBusy(true);
    const r = await apiPost<{ added: number }>(`/api/admin/sponsors/${id}/benefits`, { seed: true });
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('sp_failed'));
      return;
    }
    setMsg(t('sp_seeded', { n: r.data?.added ?? 0 }));
    await load();
  }

  async function addBenefit() {
    if (!item.trim()) return;
    setBusy(true);
    const r = await apiPost(`/api/admin/sponsors/${id}/benefits`, { item: item.trim() });
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('sp_failed'));
      return;
    }
    setItem('');
    await load();
  }

  async function toggleBenefit(b: BenefitView) {
    const r = await apiPatch(`/api/admin/sponsors/${id}/benefits`, { benefit_id: b.id, done: b.done ? false : true });
    if (!r.ok) setErr(errOf(r.data) || t('sp_failed'));
    else await load();
  }

  async function removeBenefit(benefitId: string) {
    const r = await apiDelete(`/api/admin/sponsors/${id}/benefits?benefitId=${benefitId}`);
    if (!r.ok) setErr(errOf(r.data) || t('sp_failed'));
    else await load();
  }

  async function suggest() {
    setBusy(true);
    setErr('');
    setMsg(t('sp_ai_suggesting'));
    const r = await apiPost<{ suggestion: { stage: string; next_action: string; reason: string }; thirdParty: boolean }>(
      `/api/admin/sponsors/${id}/suggest`,
      {},
    );
    setBusy(false);
    setMsg('');
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('sp_failed'));
      return;
    }
    setSug(r.data.suggestion);
  }

  async function applySuggestion() {
    if (!sug) return;
    await patch({ stage: sug.stage, next_action: sug.next_action });
    setSug(null);
  }

  async function thankLetter() {
    setBusy(true);
    setErr('');
    const r = await apiPost('/api/admin/sponsors/' + id + '/thank-letter', {});
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('sp_failed'));
      return;
    }
    setMsg(t('sp_thank_ok'));
  }

  async function removeSponsor() {
    if (!window.confirm(t('confirm_delete'))) return;
    const r = await apiDelete(`/api/admin/sponsors/${id}`);
    if (!r.ok) setErr(errOf(r.data) || t('sp_failed'));
    else nav('/admin/sponsors');
  }

  if (!s) {
    return (
      <div className="max-w-3xl">
        <p className="text-sm" style={{ color: err ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
          {err || '…'}
        </p>
      </div>
    );
  }

  const done = benefits.filter((b) => b.done).length;

  return (
    <div className="max-w-3xl">
      <Link to="/admin/sponsors" className="text-sm" style={{ color: 'var(--color-primary)' }}>
        ← {t('sp_back')}
      </Link>

      <h2 className="font-bold text-lg mt-2 mb-1">{s.company}</h2>
      <p className="text-xs mb-3" style={{ color: 'var(--color-text-secondary)' }}>
        {t('sp_stage')}：<b>{t(`sp_stage_${s.stage}`)}</b> ｜ {t('sp_pledge')}：{money(s.pledge_amount)} ｜ {t('sp_paid')}：{money(s.paid_amount)} ｜{' '}
        {t('sp_outstanding')}：
        <b style={{ color: s.pledge_amount > s.paid_amount ? 'var(--color-danger)' : undefined }}>
          {money(Math.max(0, s.pledge_amount - s.paid_amount))}
        </b>
      </p>

      {/* ── 推進階段 ─────────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-4" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' }}>
        <h3 className="font-bold mb-2">{t('sp_stage')}</h3>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((st) => (
            <button
              key={st}
              type="button"
              disabled={busy}
              onClick={() => void patch({ stage: st, paid_amount: st === 'paid' ? s.pledge_amount : s.paid_amount })}
              className="rounded-lg px-3 py-1 text-sm font-semibold"
              style={{
                ...box,
                cursor: 'pointer',
                ...(s.stage === st ? { background: 'var(--color-primary)', color: 'var(--color-on-primary)', borderColor: 'var(--color-primary)' } : {}),
              }}
            >
              {t(`sp_stage_${st}`)}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => void suggest()} className="rounded-lg px-3 py-1.5 text-sm font-semibold" style={btn}>
            {t('sp_ai_suggest')}
          </button>
          <button type="button" disabled={busy} onClick={() => void thankLetter()} className="rounded-lg px-3 py-1.5 text-sm" style={box}>
            {t('sp_thank_letter')}
          </button>
          {sug && (
            <div className="rounded-lg p-2 mt-2" style={{ border: '1px solid var(--color-primary)' }}>
              <p className="text-sm">
                {t('sp_suggest', { stage: t(`sp_stage_${sug.stage}`) })}
              </p>
              {sug.next_action && <p className="text-sm mt-1">→ {sug.next_action}</p>}
              {sug.reason && (
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('sp_suggest_reason', { reason: sug.reason })}
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <button type="button" disabled={busy} onClick={() => void applySuggestion()} className="rounded-lg px-3 py-1 text-sm font-semibold" style={btn}>
                  {t('sp_ai_apply')}
                </button>
                <button type="button" onClick={() => setSug(null)} className="text-sm" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                  {t('btn_cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── 金額 ＋ 跟進 ─────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-4" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' }}>
        <h3 className="font-bold mb-2">{t('sp_amounts')}</h3>
        <div className="grid grid-cols-2 gap-2">
          <input
            style={field}
            inputMode="numeric"
            placeholder={t('sp_pledge')}
            defaultValue={String(s.pledge_amount)}
            onBlur={(e) => void patch({ pledge_amount: Number(e.target.value.replace(/[^0-9]/g, '')) })}
          />
          <input
            style={field}
            inputMode="numeric"
            placeholder={t('sp_paid')}
            defaultValue={String(s.paid_amount)}
            onBlur={(e) => void patch({ paid_amount: Number(e.target.value.replace(/[^0-9]/g, '')) })}
          />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input style={field} placeholder={t('sp_owner_code')} defaultValue={s.owner_code} onBlur={(e) => void patch({ owner_code: e.target.value.trim() })} />
          <input style={field} placeholder={t('sp_next_action')} defaultValue={s.next_action} onBlur={(e) => void patch({ next_action: e.target.value.trim() })} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input style={field} placeholder={t('sp_contact_name')} defaultValue={s.contact_name} onBlur={(e) => void patch({ contact_name: e.target.value.trim() })} />
          <input style={field} placeholder={t('sp_contact_email')} defaultValue={s.contact_email} onBlur={(e) => void patch({ contact_email: e.target.value.trim() })} />
        </div>
        <textarea
          style={{ ...field, marginTop: 8 }}
          rows={3}
          placeholder={t('sp_note')}
          defaultValue={s.note}
          onBlur={(e) => void patch({ note: e.target.value })}
        />
      </section>

      {/* ── 權益 checklist ───────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-4" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' }}>
        <h3 className="font-bold mb-1">
          {t('sp_benefits')}（{done}/{benefits.length}）
        </h3>
        <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
          {t('sp_benefits_hint')}
        </p>
        <ul className="text-sm mb-2">
          {benefits.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            benefits.map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-2 py-1">
                <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={b.done === 1} onChange={() => void toggleBenefit(b)} />
                  <span style={{ textDecoration: b.done ? 'line-through' : undefined }}>{b.item}</span>
                </label>
                <button
                  type="button"
                  onClick={() => void removeBenefit(b.id)}
                  className="text-xs"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
                >
                  {t('btn_delete')}
                </button>
              </li>
            ))
          )}
        </ul>
        {tierBenefits.length > 0 && (
          <button type="button" disabled={busy} onClick={() => void seedBenefits()} className="rounded-lg px-3 py-1.5 text-sm font-semibold mb-2" style={btn}>
            {t('sp_seed', { n: tierBenefits.length })}
          </button>
        )}
        <div className="flex gap-2">
          <input style={field} placeholder={t('sp_add_benefit')} value={item} onChange={(e) => setItem(e.target.value)} />
          <button type="button" disabled={busy} onClick={() => void addBenefit()} className="rounded-lg px-3 py-1.5 text-sm font-semibold" style={btn}>
            {t('btn_new')}
          </button>
        </div>
      </section>

      {msg && <p className="text-sm mb-2" style={{ color: 'var(--color-primary)' }}>{msg}</p>}
      {err && <p className="text-sm mb-2" style={{ color: 'var(--color-danger)' }}>{err}</p>}
      <button
        type="button"
        onClick={() => void removeSponsor()}
        className="text-sm mb-4"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
      >
        {t('sp_delete')}
      </button>
    </div>
  );
}
